/**
 * WebGL renderer for the Mandelbrot set.
 * Uses GPU parallelism for much higher throughput than CPU/workers.
 * Falls back to canvas 2D when WebGL is unavailable.
 *
 * Mandelbrot: x' = x² - y² + x0, y' = 2xy + y0. Escape when x²+y² ≥ 4.
 * Coloring matches fractal_math.js (mu-based smooth coloring).
 */

const MAX_ITERATIONS = 255;
const ESCAPE_THRESHOLD_SQ = 4.0;

const VERTEX_SHADER_SRC = `
  attribute vec2 a_position;
  varying vec2 v_uv;
  void main() {
    v_uv = 0.5 * (a_position + 1.0);
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER_SRC = `
  precision highp float;
  varying vec2 v_uv;
  uniform vec2 u_xRange;
  uniform vec2 u_yRange;
  uniform float u_r;
  uniform float u_gScale;
  uniform float u_b;
  uniform float u_aScale;
  uniform float u_useMu;
  uniform float u_useFixedAlpha;
  uniform float u_aFixed;

  void main() {
    float x0 = mix(u_xRange.x, u_xRange.y, v_uv.x);
    // Flip Y to match the 2D canvas orientation (top row = yRange.x)
    float y0 = mix(u_yRange.y, u_yRange.x, v_uv.y);

    // Mandelbrot: z² + c; start with x = y = 0
    float x = 0.0;
    float y = 0.0;
    float x2 = 0.0;
    float y2 = 0.0;
    float distSq = 0.0;
    float iterCount = 0.0;
    bool escaped = false;

    for (int i = 0; i < 255; i++) {
      // Escape when x²+y² ≥ 4
      distSq = x2 + y2;
      if (distSq >= 4.0) {
        escaped = true;
        break;
      }

      float xnew = x2 - y2 + x0;
      float ynew = 2.0 * x * y + y0;
      x = xnew;
      y = ynew;
      x2 = x * x;
      y2 = y * y;
      iterCount += 1.0;
    }

    // If we never escaped within MAX_ITERATIONS, leave pixel transparent
    if (!escaped) {
      gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
      return;
    }

    // getMu(iterCount, distSq) from JS:
    float logDist = 0.5 * log(distSq);
    float mu = iterCount + 1.0 - log(logDist) / log(2.0);

    float val = u_useMu > 0.5 ? mu : iterCount;
    float g = clamp(val * u_gScale, 0.0, 255.0);
    float a = u_useFixedAlpha > 0.5 ? u_aFixed : clamp(val * u_aScale, 0.0, 255.0);

    gl_FragColor = vec4(u_r / 255.0, g / 255.0, u_b / 255.0, a / 255.0);
  }
`;

function compileShader(gl, type, src) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile: ${log}`);
  }
  return shader;
}

function createProgram(gl, vsSrc, fsSrc) {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link: ${log}`);
  }
  return program;
}

/**
 * Try to get a WebGL context. Prefer WebGL2, fall back to WebGL1.
 * Returns { gl, isWebGL2 } or null if not supported.
 */
export function getWebGLContext(canvas) {
  if (!canvas || typeof WebGL2RenderingContext === 'undefined') return null;
  let gl = canvas.getContext('webgl2', { alpha: false, antialias: false });
  const isWebGL2 = !!gl;
  if (!gl) {
    gl = canvas.getContext('webgl', { alpha: false, antialias: false });
  }
  if (!gl) return null;
  return { gl, isWebGL2 };
}

/**
 * Draw the Mandelbrot set with WebGL.
 * colorParams: { r, gScale, b, aScale, aFixed, useMu } (same as worker).
 * Returns true if drawn, false if WebGL failed (caller should fall back to canvas).
 */
export function drawMandelbrotFractalWebGL(canvasEl, xRange, yRange, colorParams) {
  const ctx = getWebGLContext(canvasEl);
  if (!ctx) return false;

  const { gl } = ctx;
  const w = canvasEl.width;
  const h = canvasEl.height;

  if (w <= 0 || h <= 0) return false;

  let program = canvasEl._fractalWebGLProgram;
  if (!program) {
    try {
      program = createProgram(gl, VERTEX_SHADER_SRC, FRAGMENT_SHADER_SRC);
      canvasEl._fractalWebGLProgram = program;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('WebGL program creation failed:', err.message);
      }
      return false;
    }
  }

  const cp = colorParams || {};
  const r = cp.r !== undefined ? cp.r : 255;
  const gScale = cp.gScale !== undefined ? cp.gScale : 7;
  const b = cp.b !== undefined ? cp.b : 0;
  const aScale = cp.aScale !== undefined ? cp.aScale : 15;
  const useMu = cp.useMu !== undefined ? cp.useMu : true;

  gl.viewport(0, 0, w, h);
  gl.useProgram(program);

  // Match canvas behavior: fractal drawn with alpha over a dark background.
  // Clear to fully transparent and let the CSS canvas background show through.
  gl.disable(gl.DEPTH_TEST);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);

  const u_xRange = gl.getUniformLocation(program, 'u_xRange');
  const u_yRange = gl.getUniformLocation(program, 'u_yRange');
  const u_r = gl.getUniformLocation(program, 'u_r');
  const u_gScale = gl.getUniformLocation(program, 'u_gScale');
  const u_b = gl.getUniformLocation(program, 'u_b');
  const u_aScale = gl.getUniformLocation(program, 'u_aScale');
  const u_useMu = gl.getUniformLocation(program, 'u_useMu');

  gl.uniform2f(u_xRange, xRange[0], xRange[1]);
  gl.uniform2f(u_yRange, yRange[0], yRange[1]);
  gl.uniform1f(u_r, r);
  gl.uniform1f(u_gScale, gScale);
  gl.uniform1f(u_b, b);
  const useFixedAlpha = cp.aFixed != null && cp.aFixed !== undefined;
  gl.uniform1f(u_aScale, aScale);
  gl.uniform1f(u_useMu, useMu ? 1 : 0);
  gl.uniform1f(gl.getUniformLocation(program, 'u_useFixedAlpha'), useFixedAlpha ? 1 : 0);
  gl.uniform1f(gl.getUniformLocation(program, 'u_aFixed'), useFixedAlpha ? cp.aFixed : 0);

  const aPos = gl.getAttribLocation(program, 'a_position');
  let buf = canvasEl._fractalWebGLBuffer;
  if (!buf) {
    buf = gl.createBuffer();
    canvasEl._fractalWebGLBuffer = buf;
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  const quad = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
  gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  gl.drawArrays(gl.TRIANGLES, 0, 6);
  return true;
}

/**
 * Call once per canvas when choosing render path. If this returns true,
 * the canvas has a WebGL context and future draws should use drawMandelbrotFractalWebGL.
 * If false, use getContext('2d') and the canvas/worker path.
 */
export function tryWebGL(canvasEl) {
  const ctx = getWebGLContext(canvasEl);
  if (!ctx) return false;
  canvasEl._useWebGL = true;
  return true;
}

export function useWebGL(canvasEl) {
  return !!(canvasEl && canvasEl._useWebGL);
}
